import { getAllContent } from "@/actions/getAllContent.actions";
import { API_ROUTES } from "../apiRoutes";
import { REQUEST_METHODS } from "../configs.apiClient";
import { HttpClient } from "../HttpClient";

import {
  ContentTypeEnum,
  DialectsEnum,
  LangEnum,
  OSFamilyEnum,
  MicroDramaInteractionActionEnum,
  ShowFormatEnum,
} from "@/constants/constants";
import {
  MovieDetailRes,
  PostWatchVideoEventsReq,
  ShowDetailRes,
  SignedUrlRes,
} from "@/types/consumption.types";
import {
  EpisodeDetail,
  MappedEpisodeDetail,
  MappedMovieDetail,
  MappedSeasonItem,
  MappedShowDetail,
  SimilarContentRes,
  ContentListingItemWithDuration,
  PaginationItem,
} from "@/types/contentDetails.types";
import { ContentInterface } from "@/types/home.types";
import {
  captureError,
  formatTime,
  generateContentDetailURL,
  generateURLSlug,
  getContentTypeUrlValue,
  getCurrentLang,
  getOsFamily,
  getThumbnailsFromObject,
  getTrailerURLsFromSelectedPeripheral,
  interpolateStringValues,
  isTvEnv,
} from "@/utils/common.helpers";

export class ConsumptionAPIClient {
  private httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }
  async getSimilarMicroDramaContent({
    dialect,
    contentId,
    lang,
  }: {
    dialect: string;
    contentId: number;
    lang: LangEnum;
  }): Promise<ContentInterface[]> {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.consumption.getSimilarMicroDramaContent;
      const queryParams = {
        showId: contentId,
      };
      const response = await this.httpClient.request<{
        data: ContentListingItemWithDuration[];
      }>({
        endpoint,
        options: {
          method,
        },
        dialect,
        lang,
        addToken: false,
        queryParams,
      });

      const allContent = await getAllContent(dialect as DialectsEnum, lang);

      const similarContent = response.data?.map(
        (content: ContentListingItemWithDuration, index) => {
          const slug =
            allContent?.find((item) => item._id === content._id)?.displaySlug ??
            `${content.slug}-${content._id}`;
          const contentTypeUrlValue = getContentTypeUrlValue(
            content.contentType,
            ShowFormatEnum.MICRO_DRAMA,
          );

          const contentUrl = generateContentDetailURL(
            lang as LangEnum,
            dialect,
            contentTypeUrlValue,
            slug,
          );
          const thumbnails = getThumbnailsFromObject(
            content.thumbnail,
            content.contentType === ContentTypeEnum.SHOW,
            "small",
            "ratio1",
          );
          const selectedPeripheral = content.selectedPeripheral
            ? getTrailerURLsFromSelectedPeripheral({
                contentType: content.contentType,
                selectedPeripheral: content.selectedPeripheral,
                slug: content.slug,
              })
            : null;
          return {
            _id: content._id,
            type: content.contentType,
            title: content.title,
            slug: content.slug,
            contentUrl,
            selectedPeripheral,
            description: content.description,
            duration: content.duration,
            ...thumbnails,
          };
        },
      );
      return similarContent || [];
    } catch (error) {
      console.error(
        "Error in ConsumptionApiClient getSimilarMicroDramaContent",
        error,
      );
      captureError(error);
      return [];
    }
  }
  async getSimilarContent({
    dialect,
    contentId,
    isShow,
    lang,
  }: {
    dialect: string;
    contentId: number;
    isShow: boolean;
    lang: LangEnum;
  }): Promise<ContentInterface[]> {
    try {
      const method = REQUEST_METHODS.GET;

      const endpoint = isShow
        ? API_ROUTES.consumption.getSimilarShows
        : API_ROUTES.consumption.getSimilarMovies;

      const queryParams = {
        [isShow ? "showId" : "movieId"]: contentId,
        os: OSFamilyEnum.ANDROID,
      };

      const response = await this.httpClient.request<SimilarContentRes>({
        endpoint,
        options: {
          method,
        },
        dialect,
        lang,
        addToken: false,
        queryParams,
      });

      const allContent = await getAllContent(dialect as DialectsEnum, lang);

      const similarContent = response.data?.map(
        (content: ContentListingItemWithDuration, index) => {
          const slug =
            allContent?.find((item) => item._id === content._id)?.displaySlug ??
            `${content.slug}-${content._id}`;
          const contentTypeUrlValue = getContentTypeUrlValue(
            content.contentType,
          );

          const contentUrl = generateContentDetailURL(
            lang as LangEnum,
            dialect,
            contentTypeUrlValue,
            slug,
          );
          const thumbnails = getThumbnailsFromObject(
            content.thumbnail,
            content.contentType === ContentTypeEnum.SHOW,
            "small",
            "ratio1",
          );
          const selectedPeripheral = content.selectedPeripheral
            ? getTrailerURLsFromSelectedPeripheral({
                contentType: content.contentType,
                selectedPeripheral: content.selectedPeripheral,
                slug: content.slug,
              })
            : null;
          return {
            _id: content._id,
            type: content.contentType,
            title: content.title,
            slug: content.slug,
            contentUrl,
            selectedPeripheral,
            description: content.description,
            duration: content.duration,
            ...thumbnails,
          };
        },
      );

      return similarContent || [];
    } catch (error) {
      console.error("Error in ConsumptionApiClient getSimilarContent", error);
      captureError(error);
      return [];
    }
  }

  async getShowDetails({
    titleId,
    dialect,
    lang,
    seasonId,
    addToken,
    pagination,
  }: {
    titleId: number;
    dialect: string;
    lang?: string;
    seasonId?: string | null;
    addToken?: boolean;
    pagination?: PaginationItem;
  }) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.consumption.getShowDetails;
      const finalLang = lang || (await getCurrentLang());

      const queryParams: Record<string, string | number | boolean> = {
        showId: titleId,
        seasonId: seasonId || "",
      };
      if (pagination) {
        // For pagination, "startEpisodeNumber" is inclusive (0-based index) and "endEpisodeNumber" is exclusive.
        // For example, to fetch episodes 1 to 5, use startEpisodeNumber = 0 and endEpisodeNumber = 5.
        queryParams.startEpisodeNumber = pagination.pageStartIndex - 1;
        queryParams.endEpisodeNumber = pagination.pageEndIndex;
      }
      const response = await this.httpClient.nestRequest<ShowDetailRes>({
        endpoint,
        options: {
          method,
          headers: {
            os: OSFamilyEnum.ANDROID,
          },
        },
        queryParams,
        dialect,
        lang: finalLang,
        apiVersionHeaderValue: "2",
        addToken,
      });

      // TODO: Remove this once the API returns the English title. Currently, we are making an additional API call to get the English title for URL slugs.
      let englishResponse: ShowDetailRes;
      if (finalLang == LangEnum.EN) {
        englishResponse = response;
      } else {
        englishResponse = await this.httpClient.nestRequest<ShowDetailRes>({
          endpoint,
          options: {
            method,
            headers: {
              os: OSFamilyEnum.ANDROID,
            },
          },
          queryParams,
          dialect,
          lang: LangEnum.EN,
          apiVersionHeaderValue: "2",
          addToken,
        });
      }

      const { title, description } = response.data;
      const episodeList = response.data.episodes;

      const { title: englishTitle } = englishResponse.data;
      const mappedEpisodeList = await this.getFormattedEpisodeList({
        episodeList,
        dialect,
      });

      const slug = generateURLSlug({
        contentName: englishTitle,
        fallbackSlug: response.data.slug || "",
        contentId: titleId,
      });

      let lastSeenEpisodeIndex: number | null = null;
      let lastSeenSeasonNumber: number | null = null;

      const seasonsList: MappedSeasonItem[] = response.seasonList;

      let showMetaData = { ...response.data, title };

      const lastSeenDetail = response.data.lastSeenDetail;

      await Promise.all(
        // fetch all seasons data
        seasonsList.map(async (season) => {
          const queryParams = {
            showId: titleId,
            seasonId: season.seasonOrder === 1 ? "" : season.id,
          };

          const newSeasonResponse =
            await this.httpClient.nestRequest<ShowDetailRes>({
              endpoint,
              options: {
                method,
                headers: {
                  os: OSFamilyEnum.ANDROID,
                },
              },
              queryParams,
              dialect,
              lang: finalLang,
              apiVersionHeaderValue: "2",
              addToken,
            });

          // TODO: Remove this once the API returns the English title. Currently, we are making an additional API call to get the English title for URL slugs.
          let newSeasonEnglishRes: ShowDetailRes;
          if (finalLang == LangEnum.EN) {
            newSeasonEnglishRes = newSeasonResponse;
          } else {
            newSeasonEnglishRes =
              await this.httpClient.nestRequest<ShowDetailRes>({
                endpoint,
                options: {
                  method,
                  headers: {
                    os: OSFamilyEnum.ANDROID,
                  },
                },
                queryParams,
                dialect,
                lang: LangEnum.EN,
                apiVersionHeaderValue: "2",
                addToken,
              });
          }

          const { title: englishTitle } = newSeasonEnglishRes.data;

          const mappedEpisodeList = await this.getFormattedEpisodeList({
            episodeList: newSeasonResponse.data.episodes,
            dialect,
          });

          season.episodes = mappedEpisodeList;
          season.seasonSlug = newSeasonResponse.data.seasonSlug;
          season.selectedPeripheral = newSeasonResponse.data.selectedPeripheral;

          // check if the last seen episode is in the current season
          const isLastSeenEpisodeInCurrentSeason =
            lastSeenDetail &&
            lastSeenDetail.seasonId &&
            lastSeenDetail.episodeSlug &&
            lastSeenDetail.seasonId === season.id;

          if (
            lastSeenEpisodeIndex === null &&
            isLastSeenEpisodeInCurrentSeason
          ) {
            // update the show metadata with the last seen season data
            showMetaData = {
              ...newSeasonResponse.data,
              title: englishTitle,
            };

            lastSeenEpisodeIndex = newSeasonEnglishRes.data.episodes.findIndex(
              (item) => item.slug === lastSeenDetail.episodeSlug,
            );
            lastSeenSeasonNumber = season.seasonOrder;
          }
        }),
      );

      const trailer = response.data.selectedPeripheral
        ? getTrailerURLsFromSelectedPeripheral({
            contentType: ContentTypeEnum.SHOW,
            selectedPeripheral: response.data.selectedPeripheral,
            slug: response.data.slug,
          })
        : null;

      const selectedPeripheral = response.data.selectedPeripheral;

      const trailerThumbnailSourceLink = selectedPeripheral
        ? selectedPeripheral.thumbnail?.horizontal?.sourceLink
        : response.data.thumbnail.horizontal?.ratio1.sourceLink;

      const trailerThumbnail = `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/show/horizontal/small/${trailerThumbnailSourceLink}`;

      const showDetails: MappedShowDetail = {
        ...showMetaData,
        slug,
        originalSlug: response?.data.slug, // The slug that was added in the CMS.
        episodes: mappedEpisodeList,
        description,
        trailerThumbnail,
        largeTrailerThumbnail: `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/show/horizontal/large/${response.data.thumbnail.horizontal?.ratio1.sourceLink}`,
        trailer,
        lastSeenDetail:
          response.data.lastSeenDetail && lastSeenEpisodeIndex !== null
            ? {
                ...response.data.lastSeenDetail,
                episodeNumber: lastSeenEpisodeIndex,
                seasonNumber: lastSeenSeasonNumber,
              }
            : undefined,
        seasonsList,
      };

      return showDetails;
    } catch (error) {
      console.error("Error in Consumption API Client getShowDetails", error);
      throw captureError(error);
    }
  }

  async getFormattedEpisodeList({
    episodeList,
    dialect,
  }: {
    episodeList: EpisodeDetail[];
    dialect: string;
  }) {
    return episodeList.map((episode) => {
      const thumbnail = episode?.thumbnail?.horizontal?.ratio1?.sourceLink;
      const formattedDuration = formatTime(episode.duration, "hm");
      const {
        introEndTime,
        introStartTime,
        nextEpisodeNudgeStartTime,
        language,
        displayLanguage,
        genreList,
      } = episode;

      return {
        ...episode,
        _id: episode._id,
        title: episode.title,
        description: episode.description,
        duration: episode.duration,
        formattedDuration,
        slug: episode.slug,
        isPremium: episode.isPremium,
        episodeOrder: episode.episodeOrder,
        videoFormatDetail: episode.videoFormatDetail,
        horizontalThumbnailURL: thumbnail
          ? `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/episode/horizontal/small/${thumbnail}`
          : "",
        subtitle: episode.subtitle,
        introEndTime,
        introStartTime,
        nextEpisodeNudgeStartTime,
        language,
        displayLanguage,
        genreList,
        thumbnail: episode.thumbnail,
      };
    }) as MappedEpisodeDetail[];
  }

  async getEpisodeSignedUrl({
    episodeId,
    dialect,
    lang,
  }: {
    episodeId: number;
    dialect: string;
    lang: string;
  }) {
    const method = REQUEST_METHODS.GET;
    const endpoint = API_ROUTES.consumption.getSignedUrl;

    const queryParams = {
      episodeId,
      cdnType: "aws-cloudfront",
      isRecurringSubscriptionBuild: "true",
      isTrialSubscriptionBuildUser: "true",
      appId: "in.stage.dev",
    };

    const response = await this.httpClient.request<{
      data: SignedUrlRes;
    }>({
      endpoint,
      options: {
        method,
      },
      queryParams,
      addToken: true,
      lang,
      dialect,
    });

    return response?.data;
  }

  async getEpisodeSignedUrlWithoutLogin({
    episodeId,
    dialect,
    lang,
    userId,
  }: {
    episodeId: number;
    dialect: string;
    lang: string;
    userId?: string;
  }) {
    const method = REQUEST_METHODS.GET;
    const endpoint = API_ROUTES.consumption.getEpisodeSignedUrlWithoutLogin(
      episodeId.toString(),
    );

    const queryParams: Record<string, string> = {
      cdnType: "aws-cloudfront",
      isRecurringSubscriptionBuild: "true",
      isTrialSubscriptionBuildUser: "true",
      appId: "in.stage.dev",
      ...(userId && { userId }),
    };

    const response = await this.httpClient.nestRequest<SignedUrlRes>({
      endpoint,
      options: {
        method,
        headers: {
          "x-internal-api-secret":
            process.env.NEXT_PUBLIC_INTERNAL_API_SECRET || "",
        },
      },
      queryParams,
      addToken: false,
      lang,
      dialect,
    });

    return response;
  }

  async getMovieDetails({
    movieId,
    dialect,
    lang,
    addToken,
  }: {
    movieId: number;
    dialect: string;
    lang: string;
    addToken?: boolean;
  }) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.consumption.getMovieDetails;
      const finalLang = lang || (await getCurrentLang());

      const queryParams = {
        movieId,
      };
      const response = await this.httpClient.nestRequest<MovieDetailRes>({
        endpoint,
        options: {
          method,
          headers: {
            os: OSFamilyEnum.ANDROID,
          },
        },
        queryParams,
        dialect,
        lang: finalLang,
        addToken,
      });

      // TODO: Remove this once the API returns the English title. Currently, we are making an additional API call to get the English title for URL slugs.
      let englishResponse: MovieDetailRes;
      if (finalLang == LangEnum.EN) {
        englishResponse = response;
      } else {
        englishResponse = await this.httpClient.nestRequest<MovieDetailRes>({
          endpoint,
          options: {
            method,
            headers: {
              os: OSFamilyEnum.ANDROID,
            },
          },
          queryParams,
          dialect,
          lang: LangEnum.EN,
          addToken,
        });
      }

      const movie = response.movie;
      const { title, description } = movie;
      const { title: englishTitle } = englishResponse.movie;

      const slug = generateURLSlug({
        contentName: englishTitle,
        fallbackSlug: response.movie.slug || "",
        contentId: movieId,
      });

      const trailer = movie.selectedPeripheral
        ? getTrailerURLsFromSelectedPeripheral({
            contentType: ContentTypeEnum.MOVIE,
            selectedPeripheral: movie.selectedPeripheral,
            slug: movie.slug,
          })
        : null;

      const selectedPeripheral = movie.selectedPeripheral;

      const trailerThumbnailSourceLink = selectedPeripheral
        ? selectedPeripheral.thumbnail?.horizontal?.sourceLink
        : movie.thumbnail.horizontal?.ratio1.sourceLink;

      const trailerThumbnail = `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/episode/horizontal/small/${trailerThumbnailSourceLink}`;

      const showDetails: MappedMovieDetail = {
        ...movie,
        title,
        slug,
        originalSlug: movie.slug, // The slug that was added in the CMS.
        description,
        trailerThumbnail,
        largeTrailerThumbnail: `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/episode/horizontal/large/${movie.thumbnail.horizontal?.ratio1.sourceLink}`,
        videoFormatDetail: movie.videoFormatDetails,
        trailer,
      };

      return showDetails;
    } catch (error) {
      console.error("Error in Consumption API Client getMovieDetails", error);
      throw captureError(error);
    }
  }

  async postWatchVideoEvents({
    dialect,
    lang,
    payload,
    token,
  }: {
    dialect: string;
    lang: string;
    payload: PostWatchVideoEventsReq;
    token: string;
  }) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.consumption.postWatchVideoEvents;
      const finalLang = lang || (await getCurrentLang());

      const response = await this.httpClient.paatarServerRequest<{}>({
        endpoint,
        options: {
          method,
          // keepalive is set to false for tv env as it is not supported in Tizen tv
          keepalive: isTvEnv() ? false : true, // Ensures request completes even if browser tab is closed
        },
        dialect,
        lang: finalLang,
        body: payload,
        token,
      });
    } catch (error) {
      console.error(
        "Error in Consumption API Client postWatchVideoEvents",
        error,
      );
      throw captureError(error);
    }
  }

  async microDramaInteraction(payload: {
    episodeSlug: string;
    showSlug: string;
    action: MicroDramaInteractionActionEnum;
  }) {
    const method = REQUEST_METHODS.PATCH;
    const endpoint = API_ROUTES.consumption.microDramaInteraction;

    try {
      const response = await this.httpClient.nestRequest<{}>({
        endpoint,
        options: {
          method,
        },
        body: payload,
        addToken: true,
      });

      return true;
    } catch (error) {
      console.error(
        "Error in Consumption API Client microDramaInteraction",
        error,
      );
      captureError(error);
      return false;
    }
  }
}
